import { AUTOMATION_STEP_TYPES, type AutomationStepType } from '@marketing-hub/shared';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import leadStyles from '@/features/leads/Leads.module.css';
import { AUTOMATION_STEP_LABELS } from './automationUi';
import {
  AutomationStepConfigEditor,
  type EditableAutomationStep,
} from './AutomationStepConfigEditor';
import styles from './automations.module.css';

type AgentOption = { id: string; name: string; enabled: boolean };

type Props = {
  steps: EditableAutomationStep[];
  agents: AgentOption[];
  onChange: (steps: EditableAutomationStep[]) => void;
};

function reindex(steps: EditableAutomationStep[]): EditableAutomationStep[] {
  return steps.map((s, i) => ({ ...s, step_order: i }));
}

export function AutomationStepListEditor({ steps, agents, onChange }: Props) {
  function updateStep(index: number, next: EditableAutomationStep) {
    onChange(steps.map((s, i) => (i === index ? next : s)));
  }

  function addStep(type: AutomationStepType) {
    if (steps.length >= 20) return;
    onChange(
      reindex([
        ...steps,
        {
          step_order: steps.length,
          step_type: type,
          config: type === 'generate_analytics_report' ? { days: 14 } : {},
          enabled: true,
        },
      ]),
    );
  }

  function move(index: number, delta: -1 | 1) {
    const target = index + delta;
    if (target < 0 || target >= steps.length) return;
    const copy = [...steps];
    const [item] = copy.splice(index, 1);
    copy.splice(target, 0, item!);
    onChange(reindex(copy));
  }

  return (
    <div>
      {steps.map((step, index) => (
        <div key={`${step.step_order}-${index}`} className={styles.stepCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
            <strong>
              Stap {step.step_order}: {AUTOMATION_STEP_LABELS[step.step_type]}
            </strong>
            <div>
              <Button type="button" variant="ghost" disabled={index === 0} onClick={() => move(index, -1)}>
                ↑
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={index >= steps.length - 1}
                onClick={() => move(index, 1)}
              >
                ↓
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={steps.length <= 1}
                onClick={() => onChange(reindex(steps.filter((_, i) => i !== index)))}
              >
                Verwijder
              </Button>
            </div>
          </div>
          <Field label="Type">
            <select
              className={leadStyles.filterInput}
              value={step.step_type}
              onChange={(e) =>
                updateStep(index, {
                  ...step,
                  step_type: e.target.value as AutomationStepType,
                  config: {},
                })
              }
            >
              {AUTOMATION_STEP_TYPES.map((t) => (
                <option key={t} value={t}>
                  {AUTOMATION_STEP_LABELS[t]}
                </option>
              ))}
            </select>
          </Field>
          <AutomationStepConfigEditor
            step={step}
            agents={agents}
            onChange={(next) => updateStep(index, next)}
          />
        </div>
      ))}
      <Field label="Stap toevoegen">
        <select
          className={leadStyles.filterInput}
          defaultValue=""
          onChange={(e) => {
            const v = e.target.value as AutomationStepType;
            if (v) addStep(v);
            e.target.value = '';
          }}
        >
          <option value="">— type —</option>
          {AUTOMATION_STEP_TYPES.map((t) => (
            <option key={t} value={t}>
              {AUTOMATION_STEP_LABELS[t]}
            </option>
          ))}
        </select>
      </Field>
    </div>
  );
}
